import { api, writeDB } from './store';
import { Address } from './company';
import { getOrder } from './order';
import { Document } from './document';
import { apollo } from './graphql';
import { POLL_INVOICE_DOCUMENT_URL, UNBOOK_INVOICE } from './queries/invoice';
import { wait } from '../util/util';

/* INTERFACE */
export type InvoiceStatus = 'draft' | 'pending' | 'booked' | 'canceled';

export interface Invoice extends Document {
  bookedAt: Date;
  bookingNumber: string;
  formattedBookingNumber: string;
  canceledAt: Date;
  paidAt: Date;
  status: InvoiceStatus;
  taxes: string[];
  total: number;
}

/* INVOICE CREATE */
export async function createInvoice(orderId: string, address?: Address): Promise<Invoice> {
  const response = await api.createInvoice({ orderId, address });
  // Create Invoice doesn't return the full Invoice.
  return response.data as unknown as Invoice;
}

/* INVOICE UPDATE */
export async function updateInvoice({
  orderId,
  invoiceId,
  status,
  address,
  skipEmailDelivery,
}: {
  orderId?: string;
  invoiceId: string;
  status?: string;
  address?: Address;
  skipEmailDelivery?: boolean;
}): Promise<void> {
  await api.updateInvoice({
    invoiceId,
    status,
    address,
    skipEmailDelivery,
  });
  if (orderId) {
    await getOrder(orderId);
  }
}
/* INVOICE DELETE */
export async function deleteInvoice(orderId: string, invoiceId: string): Promise<void> {
  const order = writeDB.table('orders').find(orderId);
  if (order) {
    await api.destroyInvoice({ invoiceId });
    await getOrder(order.id);
  }
}

export async function pollInvoiceUntilUrl(invoice: Invoice, setIsPollingUrl: (_: boolean) => void): Promise<void> {
  if (!invoice.orderId) return;

  const result = await apollo.query({
    query: POLL_INVOICE_DOCUMENT_URL,
    variables: { invoiceId: invoice.id },
    fetchPolicy: 'network-only',
  });

  if (!result.data.invoice?.documentUrl) {
    await wait(2);
    return pollInvoiceUntilUrl(invoice, setIsPollingUrl);
  }

  const order = writeDB.table('orders').find(invoice.orderId);
  if (order) {
    const orderInvoice = order?.invoices.find((i) => i.id === invoice.id);
    if (orderInvoice) {
      orderInvoice.documentUrl = result.data.invoice.documentUrl;
      writeDB.table('orders').update(order, order);
    }
  }
  setIsPollingUrl(false);
}

export async function unbookInvoice(invoiceId: string): Promise<void> {
  await apollo.mutate({
    mutation: UNBOOK_INVOICE,
    variables: { invoiceId: invoiceId },
  });
}
